import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageLoader } from '@shared/components/PageLoader';
import { projectAPI } from '@/utils/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import type { WeeklyReportSummary } from '@/types/report';

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' };
  return `Week of ${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', yearOpts)}`;
}

function formatPublishedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ReportsPage() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [reports, setReports] = useState<WeeklyReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = permissions.isSuperAdmin || permissions.isOwner;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await projectAPI.getReports();
        setReports(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        console.error('Failed to load reports:', message);
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const publishedReports = reports.filter(r => r.status === 'published');
  const draftReports = reports.filter(r => r.status === 'draft');

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
          <FileText className="h-6 w-6" />
          Weekly Reports
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Friday owner briefings | Confidential
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive font-medium">Failed to load reports</p>
          <p className="text-xs text-muted-foreground mt-1">{error}</p>
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : (
        <>
          {/* Draft Reports (admin only) */}
          {isAdmin && draftReports.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                Drafts
                <Badge variant="secondary" className="text-xs">
                  {draftReports.length}
                </Badge>
              </h2>
              <div className="space-y-2">
                {draftReports.map((report) => (
                  <div
                    key={report.id}
                    className="group flex items-center justify-between rounded-md border border-warning/30 bg-card p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15">
                        <Calendar className="h-4 w-4 text-warning" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {formatDateRange(report.week_start_date, report.week_end_date)}
                        </p>
                        <p className="text-xs text-muted-foreground">Draft — awaiting review</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs">
                        Draft
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Published Reports */}
          <div>
            {isAdmin && draftReports.length > 0 && (
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Published</h2>
            )}
            {publishedReports.length === 0 && draftReports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 rounded-md border border-dashed border-border">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-4">
                  <FileText className="h-7 w-7 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-sm text-center max-w-md">
                  No reports published yet. The first report will appear here after the Friday brief is generated and approved.
                </p>
              </div>
            ) : publishedReports.length === 0 ? (
              <p className="text-sm text-muted-foreground">No published reports yet.</p>
            ) : (
              <div className="space-y-2">
                {publishedReports.map((report) => (
                  <div
                    key={report.id}
                    className="group flex items-center justify-between rounded-md border border-border bg-card p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {formatDateRange(report.week_start_date, report.week_end_date)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Published {report.published_at ? formatPublishedDate(report.published_at) : ''}
                          {report.published_by && ` by ${report.published_by}`}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
